/**
 * Shared shaping and totals for the Access Control Attendance Summary payload
 * (`getAccessControlSummary`). Anything showing these numbers must go through
 * here so the report and the event dashboard cannot drift apart.
 */

import { NIAGARA_ICEDOGS_ORGANIZATION_UUID } from '@blocktickets/shared-utils'

const NM_STATE_ATHLETICS_ORG_UUID = 'dbeea528-852e-43d0-b607-c8201d7a5f1c'

export const shouldShowOrganizerAccessPassScans = (organization) => {
  const uuid = String(organization?.uuid || '')
    .trim()
    .toLowerCase()
  if (uuid === NIAGARA_ICEDOGS_ORGANIZATION_UUID) return false

  return !String(organization?.name || '')
    .trim()
    .toLowerCase()
    .includes('niagara icedogs')
}

export const getAccessPassLabels = (organization) => {
  const isNmStateAthletics =
    organization?.uuid === NM_STATE_ATHLETICS_ORG_UUID ||
    String(organization?.name || '')
      .trim()
      .toLowerCase() === 'nm state athletics'

  if (isNmStateAthletics) {
    return {
      scans: 'Aggie Pass Scans',
      group: 'Aggie Passes',
    }
  }

  return {
    scans: 'Access Pass Scans',
    group: 'Access Passes',
  }
}

/** Organizer access pass scans have no ticket record, so every scan counts as admitted. */
export const buildAccessPassMetrics = (accessPassScanCount = 0) => {
  const count = Number(accessPassScanCount || 0)
  return {
    ticketsSold: count,
    ticketProceeds: 0,
    scanned: count,
    unScanned: 0,
  }
}

function sortSectionNumber(a, b) {
  return a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

// Sort object by keys with custom comparator
function sortObjectByKeys(obj, comparator) {
  const sorted = {}
  const keys = Object.keys(obj).sort(comparator)

  for (const key of keys) {
    sorted[key] = obj[key]
  }

  return sorted
}

// Sort subGroups alphabetically by source
function sortSubGroups(subGroups) {
  return sortObjectByKeys(subGroups, (a, b) => {
    const sourceA = subGroups[a].source || a
    const sourceB = subGroups[b].source || b
    return sourceA.localeCompare(sourceB)
  })
}

// Sort subGroups with Standard admission first, then Complimentary, then alphabetical
function sortSubGroupsWithComplimentaryAndStandard(subGroups) {
  return sortObjectByKeys(subGroups, (a, b) => {
    const sourceA = subGroups[a].source || a
    const sourceB = subGroups[b].source || b

    const isStandardA = sourceA.toLowerCase().includes('standard admission')
    const isStandardB = sourceB.toLowerCase().includes('standard admission')
    const isComplimentaryA = sourceA.toLowerCase().includes('complimentary')
    const isComplimentaryB = sourceB.toLowerCase().includes('complimentary')

    // Standard admission always first
    if (isStandardA && !isStandardB) return -1
    if (!isStandardA && isStandardB) return 1

    // Complimentary second (after standard admission)
    if (isComplimentaryA && !isComplimentaryB) return -1
    if (!isComplimentaryA && isComplimentaryB) return 1

    // Otherwise sort alphabetically
    return sourceA.localeCompare(sourceB)
  })
}

// Sort subGroups with "Standard" items first, then alphabetically
function sortSubGroupsWithStandard(subGroups) {
  return sortObjectByKeys(subGroups, (a, b) => {
    const sourceA = subGroups[a].source || a
    const sourceB = subGroups[b].source || b

    const hasStandardA =
      sourceA.includes('(Standard)') || sourceA.includes('= Standard')
    const hasStandardB =
      sourceB.includes('(Standard)') || sourceB.includes('= Standard')

    // If one has Standard and the other doesn't, Standard comes first
    if (hasStandardA && !hasStandardB) return -1
    if (!hasStandardA && hasStandardB) return 1

    // Otherwise sort alphabetically
    return sourceA.localeCompare(sourceB)
  })
}

const HIDDEN_SEASON_SEAT_GROUP_KEYS = new Set([
  'season_seat',
  'season seats',
  'season_seats',
])

function isHiddenSeasonSeatGroupKey(key) {
  return HIDDEN_SEASON_SEAT_GROUP_KEYS.has(
    String(key || '')
      .trim()
      .toLowerCase()
  )
}

function subtractGroupMetrics(parent, child) {
  if (!parent || !child) return parent
  parent.ticketsSold = Math.max(
    0,
    Number(parent.ticketsSold || 0) - Number(child.ticketsSold || 0)
  )
  parent.ticketProceeds = Math.max(
    0,
    Number(parent.ticketProceeds || 0) - Number(child.ticketProceeds || 0)
  )
  parent.scanned = Math.max(
    0,
    Number(parent.scanned || 0) - Number(child.scanned || 0)
  )
  parent.unScanned = Math.max(
    0,
    Number(parent.unScanned || 0) - Number(child.unScanned || 0)
  )
  return parent
}

/** season_seat is an internal check-in type — never show it on this report. */
function omitSeasonSeatAccessPassType(data) {
  if (!data) return data

  const next = { ...data }
  delete next.season_seat

  if (next.packages?.subGroups) {
    const subGroups = { ...next.packages.subGroups }
    const packages = { ...next.packages, subGroups }

    for (const [key, group] of Object.entries(subGroups)) {
      if (
        !isHiddenSeasonSeatGroupKey(key) &&
        !isHiddenSeasonSeatGroupKey(group?.source)
      ) {
        continue
      }
      subtractGroupMetrics(packages, group)
      delete subGroups[key]
    }

    next.packages = packages
  }

  return next
}

/**
 * Order the report sections and fold access pass scans in as their own group.
 * Totals are taken from the result, so this is also what makes access pass
 * scans count toward tickets sold and scanned.
 */
export function sortAccessControlSummary(data, organization) {
  if (!data) return data

  data = omitSeasonSeatAccessPassType(data)

  const accessPassScanCount = shouldShowOrganizerAccessPassScans(organization)
    ? Number(data.accessPassScanCount || 0)
    : 0
  const accessPassMetrics = buildAccessPassMetrics(accessPassScanCount)
  const accessPassLabels = getAccessPassLabels(organization)

  // Define the main section order
  const sectionOrder = [
    'seasonTickets',
    'groupTickets',
    'flexPackTickets',
    'individualTickets',
    'suiteTickets',
    'clubTickets',
    'clubSeats',
    'website',
    'ticket_assignment',
    'box_office',
    'packages',
    'flex_packs',
    'flexPackages',
    'access_passes',
  ]
  const sorted = {}

  // Sort attendanceBySection by sectionNumber
  if (data.attendanceBySection || accessPassScanCount > 0) {
    const sections = { ...(data.attendanceBySection || {}) }
    if (accessPassScanCount > 0) {
      sections.access_pass_scans = {
        sectionNumber: accessPassLabels.scans,
        ...accessPassMetrics,
      }
    }

    sorted.attendanceBySection = sortObjectByKeys(sections, (a, b) => {
      if (a === 'access_pass_scans') return 1
      if (b === 'access_pass_scans') return -1
      return sortSectionNumber(
        sections[a].sectionNumber,
        sections[b].sectionNumber
      )
    })
  }

  // Add sections in the specified order
  for (const sectionKey of sectionOrder) {
    if (data[sectionKey]) {
      sorted[sectionKey] = { ...data[sectionKey] }

      if (
        (sectionKey === 'clubTickets' || sectionKey === 'suiteTickets') &&
        sorted[sectionKey].subGroups
      ) {
        const subGroups = sortObjectByKeys(
          sorted[sectionKey].subGroups,
          (a, b) => {
            if (a === 'Complimentary') return -1
            if (b === 'Complimentary') return 1
            return sortSectionNumber(a, b)
          }
        )
        if (subGroups.Complimentary?.subGroups) {
          subGroups.Complimentary = {
            ...subGroups.Complimentary,
            subGroups: sortSubGroups(subGroups.Complimentary.subGroups),
          }
        }
        sorted[sectionKey].subGroups = subGroups
      }

      if (sectionKey === 'groupTickets' && sorted[sectionKey].subGroups) {
        sorted[sectionKey].subGroups = sortObjectByKeys(
          sorted[sectionKey].subGroups,
          (a, b) => {
            if (a === 'Ice-Level Club') return -1
            if (b === 'Ice-Level Club') return 1
            return a.localeCompare(b)
          }
        )
      }

      if (sectionKey === 'individualTickets' && sorted[sectionKey].subGroups) {
        const channelOrder = ['Website', 'Box Office', 'Ticket Assignment']
        const customSorted = {}
        for (const key of channelOrder) {
          if (sorted[sectionKey].subGroups[key]) {
            const item = { ...sorted[sectionKey].subGroups[key] }
            if (item.subGroups) {
              item.subGroups = sortSubGroupsWithComplimentaryAndStandard(
                item.subGroups
              )
            }
            customSorted[key] = item
          }
        }
        for (const key of Object.keys(sorted[sectionKey].subGroups)) {
          if (!channelOrder.includes(key)) {
            customSorted[key] = sorted[sectionKey].subGroups[key]
          }
        }
        sorted[sectionKey].subGroups = customSorted
      }

      // Handle website: Standard admission first, then Complimentary, then alphabetical
      if (sectionKey === 'website' && sorted[sectionKey].subGroups) {
        sorted[sectionKey].subGroups =
          sortSubGroupsWithComplimentaryAndStandard(
            sorted[sectionKey].subGroups
          )
      }

      // Handle ticket_assignment: comp, individual, group (each with Standard first)
      if (sectionKey === 'ticket_assignment' && sorted[sectionKey].subGroups) {
        const ticketOrder = ['comp', 'individual', 'group']
        const customSorted = {}

        for (const key of ticketOrder) {
          if (sorted[sectionKey].subGroups[key]) {
            const item = { ...sorted[sectionKey].subGroups[key] }
            // Sort the subGroups within each category with Standard first
            if (item.subGroups) {
              item.subGroups = sortSubGroupsWithStandard(item.subGroups)
            }
            customSorted[key] = item
          }
        }

        // Add any remaining keys not in the order
        for (const key of Object.keys(sorted[sectionKey].subGroups)) {
          if (!ticketOrder.includes(key)) {
            customSorted[key] = sorted[sectionKey].subGroups[key]
          }
        }

        sorted[sectionKey].subGroups = customSorted
      }

      // Handle box_office: Standard admission first, then Complimentary, then alphabetical
      if (sectionKey === 'box_office' && sorted[sectionKey].subGroups) {
        sorted[sectionKey].subGroups =
          sortSubGroupsWithComplimentaryAndStandard(
            sorted[sectionKey].subGroups
          )
      }

      // Handle packages: alphabetical
      if (sectionKey === 'packages' && sorted[sectionKey].subGroups) {
        sorted[sectionKey].subGroups = sortSubGroups(
          sorted[sectionKey].subGroups
        )
      }

      // Handle flex_packs: alphabetical
      if (sectionKey === 'flex_packs' && sorted[sectionKey].subGroups) {
        sorted[sectionKey].subGroups = sortSubGroups(
          sorted[sectionKey].subGroups
        )
      }
    }
  }

  if (accessPassScanCount > 0) {
    sorted.access_passes = {
      title: accessPassLabels.group,
      source: 'access_passes',
      ...accessPassMetrics,
    }
  }

  // Add any remaining sections not in the order
  for (const key of Object.keys(data)) {
    if (
      !sectionOrder.includes(key) &&
      key !== 'attendanceBySection' &&
      key !== 'accessPassScanCount' &&
      key !== 'complimentaryTicketCounts' &&
      key !== 'countedTicketIds' &&
      key !== 'offerSales' &&
      key !== 'channelProceeds' &&
      key !== 'access_passes'
    ) {
      sorted[key] = data[key]
    }
  }

  sorted.accessPassScanCount = accessPassScanCount

  return sorted
}

const EXCEL_TICKET_TYPE_SKIP_KEYS = new Set([
  'attendanceBySection',
  'accessPassScanCount',
  'complimentaryTicketCounts',
  'countedTicketIds',
  'offerSales',
  'channelProceeds',
])

/**
 * Ticket-type rows for the Excel export, in the same order and depth as the
 * on-screen summary. A nested group with its own rows is a subheading.
 */
export function flattenAccessControlTicketTypes(data) {
  const flattenGroups = (groups, parentSource = null, depth = 0) =>
    Object.entries(groups || {}).flatMap(([key, group]) => {
      if (EXCEL_TICKET_TYPE_SKIP_KEYS.has(key)) return []
      if (!group || typeof group !== 'object' || Array.isArray(group)) {
        return []
      }

      const { subGroups, ...flatGroup } = group
      const children =
        subGroups && typeof subGroups === 'object' && !Array.isArray(subGroups)
          ? subGroups
          : null
      const hasChildren = Boolean(children && Object.keys(children).length)
      flatGroup.parentSource = parentSource
      flatGroup.name = key
      flatGroup.indent = depth
      flatGroup.subGroupParent = Boolean(parentSource) && hasChildren

      const nested = hasChildren
        ? flattenGroups(children, group.source || key, depth + 1)
        : []
      return [flatGroup, ...nested]
    })

  return flattenGroups(data)
}

/** TOTAL row of the report. Expects data from `sortAccessControlSummary`. */
export function calculateAccessControlTotals(eventAnalytics) {
  const totals = {
    ticketsSold: 0,
    ticketProceeds: 0,
    scanned: 0,
    unScanned: 0,
    accessPassScanCount: Number(eventAnalytics?.accessPassScanCount || 0),
  }
  const reservedKeys = new Set([
    'attendanceBySection',
    'accessPassScanCount',
    'complimentaryTicketCounts',
    'countedTicketIds',
    'offerSales',
    'channelProceeds',
  ])

  Object.entries(eventAnalytics || {}).forEach(([key, source]) => {
    if (reservedKeys.has(key)) return
    if (!source || typeof source !== 'object') return

    totals.ticketsSold += source.ticketsSold || 0
    totals.ticketProceeds += source.ticketProceeds || 0
    totals.scanned += source.scanned || 0
    totals.unScanned += source.unScanned || 0
  })

  return totals
}

function isComplimentaryGroupLabel(value) {
  return String(value || '')
    .toLowerCase()
    .includes('complimentary')
}

/**
 * Older summaries have no complimentaryTicketCounts. Read the same rows the
 * report already labels as complimentary, plus $0 assignment-type comp leaves.
 * Paid comp-tier rows keep proceeds, so they stay on the sold side.
 */
function inferComplimentaryCounts(data) {
  const counts = { sold: 0, scanned: 0 }
  const skip = new Set([
    'attendanceBySection',
    'accessPassScanCount',
    'countedTicketIds',
    'complimentaryTicketCounts',
    'offerSales',
    'channelProceeds',
  ])

  const walk = (node, key, parentKey) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return

    const childKeys = node.subGroups ? Object.keys(node.subGroups) : []
    if (childKeys.length) {
      childKeys.forEach((childKey) =>
        walk(node.subGroups[childKey], childKey, key)
      )
      return
    }

    if (node.ticketsSold == null && node.scanned == null) return

    const proceeds = Number(node.ticketProceeds || 0)
    const complimentary =
      isComplimentaryGroupLabel(key) ||
      isComplimentaryGroupLabel(node.source) ||
      isComplimentaryGroupLabel(node.title) ||
      (String(parentKey || '').toLowerCase() === 'comp' && proceeds <= 0) ||
      (String(key || '').toLowerCase() === 'comp' && proceeds <= 0)

    if (!complimentary) return
    counts.sold += Number(node.ticketsSold || 0)
    counts.scanned += Number(node.scanned || 0)
  }

  Object.entries(data || {}).forEach(([key, value]) => {
    if (skip.has(key)) return
    walk(value, key, null)
  })

  return counts
}

function money(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

function groupProceeds(group) {
  return money(group?.ticketProceeds)
}

function groupTickets(group) {
  return Math.max(0, Math.round(Number(group?.ticketsSold) || 0))
}

/**
 * Net by sales channel. Prefer the assembler field, and otherwise read the
 * same Website / Box Office / Ticket Assignment groups the report already
 * returns so the dashboard still splits proceeds when that field is absent.
 */
function resolveChannelProceeds(data) {
  const explicit = data?.channelProceeds
  const individual = data?.individualTickets?.subGroups || {}
  const fromGroups = {
    website: groupProceeds(data?.website) || groupProceeds(individual.Website),
    boxOffice:
      groupProceeds(data?.box_office) ||
      groupProceeds(individual['Box Office']),
    ticketAssignment:
      groupProceeds(data?.ticket_assignment) ||
      groupProceeds(individual['Ticket Assignment']),
    packages: money(
      groupProceeds(data?.packages) +
        groupProceeds(data?.flexPackages) +
        groupProceeds(data?.seasonTickets)
    ),
  }

  if (!explicit || typeof explicit !== 'object') return fromGroups

  return {
    website: money(explicit.website),
    boxOffice: money(explicit.boxOffice),
    ticketAssignment: money(explicit.ticketAssignment),
    packages: money(explicit.packages),
  }
}

/** Ticket counts for the same sales-channel groups as `resolveChannelProceeds`. */
function resolveChannelTickets(data) {
  const explicit = data?.channelTickets
  const individual = data?.individualTickets?.subGroups || {}
  const fromGroups = {
    website: groupTickets(data?.website) || groupTickets(individual.Website),
    boxOffice:
      groupTickets(data?.box_office) || groupTickets(individual['Box Office']),
    ticketAssignment:
      groupTickets(data?.ticket_assignment) ||
      groupTickets(individual['Ticket Assignment']),
    packages:
      groupTickets(data?.packages) +
      groupTickets(data?.flexPackages) +
      groupTickets(data?.seasonTickets),
  }

  if (!explicit || typeof explicit !== 'object') return fromGroups

  return {
    website: groupTickets({ ticketsSold: explicit.website }),
    boxOffice: groupTickets({ ticketsSold: explicit.boxOffice }),
    ticketAssignment: groupTickets({ ticketsSold: explicit.ticketAssignment }),
    packages: groupTickets({ ticketsSold: explicit.packages }),
  }
}

/** Report totals straight from a raw `getAccessControlSummary` response. */
export function getAccessControlSummaryTotals(data, organization) {
  const totals = calculateAccessControlTotals(
    sortAccessControlSummary(data, organization)
  )
  const complimentary =
    data?.complimentaryTicketCounts || inferComplimentaryCounts(data)
  const complimentarySold = Math.max(0, Number(complimentary?.sold) || 0)
  const complimentaryScanned = Math.max(0, Number(complimentary?.scanned) || 0)

  return {
    ...totals,
    complimentarySold: Math.min(complimentarySold, totals.ticketsSold),
    complimentaryScanned: Math.min(complimentaryScanned, totals.scanned),
    offerSales: Array.isArray(data?.offerSales) ? data.offerSales : [],
    channelProceeds: resolveChannelProceeds(data),
    channelTickets: resolveChannelTickets(data),
  }
}
